import { useState, type FormEvent } from 'react';
import { Send } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { feesApi } from '@/features/institute/fees/api/fees.api';
import {
  ReminderMessageField,
  smsParts,
} from '@/features/institute/fees/components/ReminderMessageField';
import { DEFAULT_CLASS_REMINDER_MESSAGE } from '@/features/institute/fees/constants';
import { useInstituteBranding } from '@/features/institute/store/branding.store';
import { ApiRequestError } from '@/lib/apiClient';
import { formatDate } from '@/lib/format';
import type { ClassFeeDue, ClassFeeReminderResult, FeeDueWhen } from '@/types/fees';
import type { MessageChannel } from '@/types/messaging';

const CHANNELS = [
  { value: 'SMS', label: 'SMS' },
  { value: 'WHATSAPP', label: 'WhatsApp' },
];

/** Who this send is for: everyone the page lists under its due-date choice, or the ticked ones. */
export type ClassReminderTarget =
  | { kind: 'ALL_DUE'; students: ClassFeeDue[]; dueWhen?: FeeDueWhen; dueWithinDays?: number }
  | { kind: 'SELECTED'; students: ClassFeeDue[] };

interface ClassFeeReminderModalProps {
  target: ClassReminderTarget;
  /** The current session's name, from the list the families came from. */
  session: string | null;
  onClose: () => void;
  onSent: () => void;
}

/**
 * A class teacher reminding their own class's families that fees are due.
 *
 * The office's reminder, without the money: the teacher never sees what a
 * family owes, so the default message names the installment and the date, and
 * `{amount}` — if they add it — is filled in by the server for each family.
 * The confirmation step is the same as the office's, for the same reason: this
 * lands on a parent's phone, costs the school money, and cannot be recalled.
 */
export function ClassFeeReminderModal({
  target,
  session,
  onClose,
  onSent,
}: ClassFeeReminderModalProps) {
  const institute = useInstituteBranding((state) => state.branding?.displayName ?? null);
  const [channel, setChannel] = useState<MessageChannel>('SMS');
  const [message, setMessage] = useState(DEFAULT_CLASS_REMINDER_MESSAGE);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [result, setResult] = useState<ClassFeeReminderResult | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);

  const count = target.students.length;
  const families = `${count} ${count === 1 ? 'family' : 'families'}`;
  const parts = smsParts(message);

  function review(event: FormEvent) {
    event.preventDefault();
    setIsConfirming(true);
  }

  async function send() {
    setIsSending(true);
    setSendError(null);
    try {
      const sent = await feesApi.sendClassReminders({
        channel,
        message: message.trim(),
        ...(target.kind === 'SELECTED'
          ? {
              scope: 'SELECTED_STUDENTS',
              studentUuids: target.students.map((student) => student.studentUuid),
            }
          : {
              scope: 'ALL_DUE',
              ...(target.dueWhen && { dueWhen: target.dueWhen }),
              ...(target.dueWithinDays !== undefined && { dueWithinDays: target.dueWithinDays }),
            }),
      });
      setResult(sent);
      setIsConfirming(false);
      onSent();
    } catch (caught) {
      // Shown verbatim inside the dialog: the server names the reason — no
      // sender set up, nobody due, a student no longer in the class.
      setSendError(
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
      setIsConfirming(false);
    } finally {
      setIsSending(false);
    }
  }

  if (result) {
    return (
      <Modal
        isOpen
        onClose={onClose}
        title="Reminders sent"
        size="sm"
        footer={
          <Button size="sm" onClick={onClose}>
            Done
          </Button>
        }
      >
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="success">{result.sent} sent</Badge>
            {result.failed > 0 && <Badge tone="critical">{result.failed} failed</Badge>}
            {result.unreachable.length > 0 && (
              <Badge tone="warning">{result.unreachable.length} no number</Badge>
            )}
          </div>
          {result.failures.length > 0 && (
            <Alert tone="critical" title="Could not be reached">
              {result.failures.map((failure) => failure.name).join(', ')}. The office can check the
              numbers on their records.
            </Alert>
          )}
          {result.unreachable.length > 0 && (
            <Alert tone="warning" title={`${result.unreachable.length} have no mobile number`}>
              {result.unreachable.map((student) => student.name).join(', ')}. Ask the office to add
              a parent's number to their record.
            </Alert>
          )}
          {result.settled.length > 0 && (
            <Alert tone="info" title={`${result.settled.length} had already paid`}>
              {result.settled.map((student) => student.name).join(', ')}. Nothing was sent to them.
            </Alert>
          )}
        </div>
      </Modal>
    );
  }

  if (isConfirming) {
    return (
      <Modal
        isOpen
        onClose={() => setIsConfirming(false)}
        size="sm"
        title={`Remind ${families}?`}
        footer={
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setIsConfirming(false)}
              disabled={isSending}
            >
              Back
            </Button>
            <Button
              size="sm"
              isLoading={isSending}
              leftIcon={<Send className="h-4 w-4" />}
              onClick={() => void send()}
            >
              {isSending ? 'Sending…' : `Send ${count === 1 ? 'reminder' : `${count} reminders`}`}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <Alert tone="warning" title="This cannot be undone">
            {families} will receive this by {channel === 'SMS' ? 'SMS' : 'WhatsApp'}
            {channel === 'SMS' && parts > 1 ? `, billed as ${parts} messages each` : ''}. There is
            no way to recall it.
          </Alert>
          {target.students[0] && (
            <div className="rounded-card border border-line bg-surface-2 p-3">
              <p className="lbl">What one family will read</p>
              <p className="mt-1 whitespace-pre-wrap text-sm text-ink">
                {preview(message, target.students[0], { institute, session })}
              </p>
            </div>
          )}
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="lg"
      title="Send fee reminders"
      description={`Goes to the parent's mobile of ${families} in your class.`}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            form="class-fee-reminder-form"
            type="submit"
            disabled={message.trim().length === 0 || count === 0}
            leftIcon={<Send className="h-4 w-4" />}
          >
            Review and send{count > 1 ? ` to ${count}` : ''}
          </Button>
        </>
      }
    >
      <form id="class-fee-reminder-form" onSubmit={review} className="flex flex-col gap-4">
        {sendError && (
          <Alert tone="critical" title="Could not send these reminders">
            {sendError}
          </Alert>
        )}
        <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,11rem)] sm:items-end">
          <div className="rounded-card border border-line bg-surface-2 px-3 py-2.5">
            <p className="lbl">Going to</p>
            <p className="mt-0.5 text-sm text-ink">
              {count === 1 ? `${target.students[0].fullName}'s parent` : families}
            </p>
          </div>
          <Select
            label="Send by"
            options={CHANNELS}
            value={channel}
            onChange={(event) => setChannel(event.target.value as MessageChannel)}
          />
        </div>
        <ReminderMessageField message={message} onChange={setMessage} channel={channel} />
      </form>
    </Modal>
  );
}

/**
 * The template as a real family on the list would read it. The amount is the
 * one thing the teacher cannot preview — it is filled in by the server — so it
 * is shown as a stand-in rather than a figure. `installment` and `dueDate` fall
 * back to the same words the server substitutes. The institute and session are
 * left as a placeholder only while they have not loaded, rather than guessed.
 */
function preview(
  template: string,
  student: ClassFeeDue,
  context: { institute: string | null; session: string | null },
): string {
  const values: Record<string, string> = {
    name: student.fullName,
    admissionNumber: student.admissionNumber,
    installment: student.installment ?? 'Fees',
    dueDate: student.dueDate ? formatDate(student.dueDate) : 'as soon as possible',
    amount: '(the amount due)',
    ...(context.institute && { institute: context.institute }),
    ...(context.session && { session: context.session }),
  };
  return template.replace(/\{([^{}]*)\}/g, (_, token: string) => values[token] ?? `their ${token}`);
}
